import React, { useEffect, useRef, useState } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { CustomTextField } from '../../../../layout/styledcomponents/CustomTextField';
import { Clear, Search } from '@mui/icons-material';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import { Box, InputAdornment, Typography, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectExperienceFilters,
    setExperienceFilters,
} from '../../../../app/Slices/ExperienceFiltersSlice';
import {
    resetExperienceSearchFilter,
    selectExperienceSearchFilter,
    setExperienceSearchFilter,
} from '../../../../app/Slices/ExperienceSearchSlice';

interface PlaceAutocompleteProps {
    onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
    height?: string;
    /** Placeholder when field doubles as experience-name search. */
    searchPlaceholder?: string;
    /** Omit the leading search (magnifying glass) icon, e.g. guest mobile layout. */
    hideSearchIcon?: boolean;
}

const PlaceAutocomplete = ({
    onPlaceSelect,
    height,
    searchPlaceholder = 'Search by location or experience',
    hideSearchIcon = false,
}: PlaceAutocompleteProps) => {
    const dispatch = useDispatch();
    const filters = useSelector(selectExperienceFilters);
    const experienceSearch = useSelector(selectExperienceSearchFilter);
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const [placeAutocomplete, setPlaceAutocomplete] =
        useState<google.maps.places.Autocomplete | null>(null);
    const [hasValue, setHasValue] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    /** After a Places pick, ignore input-driven name search briefly (formatted address). */
    const suppressSearchSyncRef = useRef(false);
    const places = useMapsLibrary('places');

    const options: google.maps.places.AutocompleteOptions = {
        fields: ['address_components', 'geometry'],
        componentRestrictions: { country: 'au' },
    };

    useEffect(() => {
        if (!places || !inputRef.current) return;
        setPlaceAutocomplete(
            new places.Autocomplete(inputRef.current, options),
        );
    }, [places]);

    useEffect(() => {
        if (!placeAutocomplete) return;
        placeAutocomplete.addListener('place_changed', () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = null;
            }
            suppressSearchSyncRef.current = true;
            onPlaceSelect(placeAutocomplete.getPlace());
            dispatch(setExperienceSearchFilter(''));
            setHasValue(Boolean(inputRef.current?.value));
            window.setTimeout(() => {
                suppressSearchSyncRef.current = false;
            }, 800);
        });
    }, [dispatch, onPlaceSelect, placeAutocomplete]);

    useEffect(() => {
        // Ensure initial state is correct if browser restores input value
        const v = inputRef.current?.value;
        const next = v != null && v !== '' ? v : experienceSearch;
        if (inputRef.current && inputRef.current.value !== next) {
            inputRef.current.value = next;
        }
        setHasValue(Boolean(next));
    }, []);

    useEffect(() => {
        if (!inputRef.current) return;
        if (suppressSearchSyncRef.current) return;
        // If the user is actively typing, don't fight the browser.
        if (document.activeElement === inputRef.current) return;
        if (inputRef.current.value === experienceSearch) return;
        inputRef.current.value = experienceSearch || '';
        setHasValue(Boolean(experienceSearch));
    }, [experienceSearch]);

    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    const syncHasValue = () => setHasValue(Boolean(inputRef.current?.value));

    const queueExperienceSearchFromInput = () => {
        if (suppressSearchSyncRef.current) return;
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            searchDebounceRef.current = null;
            if (suppressSearchSyncRef.current) return;
            const v = inputRef.current?.value ?? '';
            dispatch(setExperienceSearchFilter(v));
            // Name search: drop geo scoping so the list isn’t limited to a prior map viewport.
            if (v.trim()) {
                const f = filtersRef.current;
                dispatch(
                    setExperienceFilters({
                        ...f,
                        bounds: undefined,
                        swLat: undefined,
                        swLng: undefined,
                        neLat: undefined,
                        neLng: undefined,
                        state: [],
                        suburb: [],
                    }),
                );
            }
        }, 350);
    };

    const handleInput = () => {
        syncHasValue();
        queueExperienceSearchFromInput();
    };

    const handleClear = () => {
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        setHasValue(false);
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        dispatch(resetExperienceSearchFilter());
        dispatch(
            setExperienceFilters({
                ...filters,
                bounds: undefined,
                swLat: undefined,
                swLng: undefined,
                neLat: undefined,
                neLng: undefined,
                state: [],
                suburb: [],
            }),
        );
    };

    const theme = useTheme();

    return (
        <Box className='autocomplete-container' sx={{ width: '100%' }}>
            <Typography
                variant='body2'
                sx={{
                    fontWeight: 'medium',
                    color: theme.typography.body1.color,
                    mb: 0.35,
                }}
            >
                Where to
            </Typography>
            <CustomTextField
                hiddenLabel
                placeholder={searchPlaceholder}
                color='dark'
                variant='standard'
                fullWidth
                inputRef={inputRef}
                inputProps={{
                    autoComplete: 'none',
                    'aria-label': searchPlaceholder,
                    onInput: handleInput,
                }}
                sx={{
                    border: 0,
                    '& .MuiInputBase-input': {
                        '::placeholder': {
                            color: theme.palette.text.secondary,
                            fontWeight: '400',
                        },
                    },
                }}
                InputProps={{
                    disableUnderline: true,
                    startAdornment: hideSearchIcon ? undefined : (
                        <InputAdornment position='start'>
                            <Search color='disabled' fontSize='small' />
                        </InputAdornment>
                    ),
                    endAdornment: hasValue ? (
                        <InputAdornment position='end'>
                            <CustomIconButton
                                onClick={handleClear}
                                disableRipple
                                aria-label='Clear search'
                            >
                                <Clear sx={{ fontSize: '12px' }} />
                            </CustomIconButton>
                        </InputAdornment>
                    ) : null,
                }}
            />
        </Box>
    );
};

interface AddressAutocompleteProps {
    onChange: (bounds: google.maps.LatLngBounds, state: string) => void;
    height?: string;
    searchPlaceholder?: string;
    hideSearchIcon?: boolean;
}

const AddressSearch = ({
    onChange,
    height,
    searchPlaceholder,
    hideSearchIcon,
}: AddressAutocompleteProps) => {
    const handleSelect = (
        selectedPlace: google.maps.places.PlaceResult | null,
    ) => {
        if (!selectedPlace?.geometry?.viewport) return;
        const state = selectedPlace?.address_components?.find((c) =>
            c.types.includes('administrative_area_level_1'),
        );
        onChange(selectedPlace.geometry.viewport, state?.short_name || '');
    };

    return (
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
            <PlaceAutocomplete
                onPlaceSelect={handleSelect}
                height={height}
                searchPlaceholder={searchPlaceholder}
                hideSearchIcon={hideSearchIcon}
            />
        </APIProvider>
    );
};

export default AddressSearch;
